import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import Toast from '../components/Toast';
import { copyDay, getWaterSummary } from '../utils/nutritionApi';

function getDateRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0]
  };
}

function formatDayLabel(dateStr) {
  const date = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  if (dateStr === todayStr) return 'Today';
  if (dateStr === yesterdayStr) return 'Yesterday';

  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function History() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [range, setRange] = useState(7);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Copy state
  const [copyFromDate, setCopyFromDate] = useState(null);
  const [copyTargetDate, setCopyTargetDate] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);

  const calorieGoal = user?.daily_calorie_goal || 2000;
  const todayStr = new Date().toISOString().split('T')[0];

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange(range);

      // Fetch calorie + water summaries in parallel
      const [calRes, waterRes] = await Promise.all([
        api.get(`/api/logs/summary?startDate=${startDate}&endDate=${endDate}`),
        getWaterSummary(startDate, endDate)
      ]);

      // Build calorie data map
      const dataMap = {};
      for (const row of calRes.data) {
        const dateKey = typeof row.date === 'string' ? row.date.split('T')[0] : new Date(row.date).toISOString().split('T')[0];
        dataMap[dateKey] = {
          date: dateKey,
          total_calories: Number(row.total_calories) || 0,
          entry_count: Number(row.entry_count) || 0
        };
      }

      // Build water data map
      const waterMap = {};
      for (const row of waterRes) {
        const dateKey = typeof row.date === 'string' ? row.date.split('T')[0] : new Date(row.date).toISOString().split('T')[0];
        waterMap[dateKey] = Number(row.total_ml) || 0;
      }

      const filled = [];
      const d = new Date(getDateRange(range).startDate + 'T12:00:00');
      const endD = new Date(getDateRange(range).endDate + 'T12:00:00');
      while (d <= endD) {
        const key = d.toISOString().split('T')[0];
        const base = dataMap[key] || { date: key, total_calories: 0, entry_count: 0 };
        filled.push({ ...base, water_ml: waterMap[key] || 0 });
        d.setDate(d.getDate() + 1);
      }

      setData(filled);
    } catch (err) {
      console.error('Failed to fetch summary:', err.message);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const daysWithData = data.filter(d => d.total_calories > 0);
  const avgCalories = daysWithData.length > 0
    ? Math.round(daysWithData.reduce((sum, d) => sum + d.total_calories, 0) / daysWithData.length)
    : 0;

  const maxCal = Math.max(calorieGoal, ...data.map(d => d.total_calories));

  const getBarColor = (cal) => {
    if (cal === 0) return 'bar-empty';
    const pct = cal / calorieGoal;
    if (pct > 1) return 'bar-over';
    if (pct >= 0.8) return 'bar-near';
    return 'bar-under';
  };

  const openCopyFrom = (e, dateStr) => {
    e.stopPropagation();
    setCopyFromDate(dateStr);
    setCopyTargetDate(todayStr);
  };

  const handleCopy = async () => {
    if (!copyFromDate || copyTargetDate === copyFromDate) return;
    setCopyLoading(true);
    try {
      const newEntries = await copyDay(copyFromDate, copyTargetDate);
      setToast({ message: `Copied ${newEntries.length} item${newEntries.length !== 1 ? 's' : ''} to ${formatDayLabel(copyTargetDate)}`, type: 'success' });
      setCopyFromDate(null);
      fetchSummary();
    } catch (err) {
      console.error('Copy failed:', err.message);
      setToast({ message: 'Failed to copy day', type: 'error' });
    } finally {
      setCopyLoading(false);
    }
  };

  return (
    <div className="page history-page">
      <div className="history-header">
        <h2>History</h2>
        <div className="history-toggle">
          <button
            className={`history-toggle-btn ${range === 7 ? 'active' : ''}`}
            onClick={() => setRange(7)}
          >
            7 Days
          </button>
          <button
            className={`history-toggle-btn ${range === 30 ? 'active' : ''}`}
            onClick={() => setRange(30)}
          >
            30 Days
          </button>
        </div>
      </div>

      {/* Average */}
      <div className="card history-avg-card">
        <div className="history-avg">
          <span className="history-avg-value">{avgCalories}</span>
          <span className="history-avg-label">avg cal / day</span>
        </div>
        <div className="history-avg-meta">
          <span>Goal: {calorieGoal} cal</span>
          <span className="history-avg-sep">&middot;</span>
          <span>{daysWithData.length} of {data.length} days logged</span>
        </div>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
          <div className="loading-spinner"></div>
        </div>
      )}

      {/* Bar chart */}
      {!loading && (
        <div className="history-chart card">
          <div className="history-chart-header">
            <span className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Daily Calories vs Goal
            </span>
          </div>
          <div className="history-bars">
            {data.map(day => {
              const pct = maxCal > 0 ? (day.total_calories / maxCal) * 100 : 0;
              const goalPct = maxCal > 0 ? (calorieGoal / maxCal) * 100 : 0;
              const barColor = getBarColor(day.total_calories);
              const dateObj = new Date(day.date + 'T12:00:00');
              const shortLabel = range <= 7
                ? dateObj.toLocaleDateString('en-US', { weekday: 'short' })
                : dateObj.getDate().toString();

              return (
                <div
                  key={day.date}
                  className="history-bar-col"
                  onClick={() => navigate(day.date === todayStr ? '/log' : `/log/${day.date}`)}
                >
                  <div className="history-bar-track">
                    <div className="history-goal-line" style={{ bottom: `${goalPct}%` }} />
                    <div className={`history-bar-fill ${barColor}`} style={{ height: `${pct}%` }} />
                  </div>
                  <span className="history-bar-label">{shortLabel}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day list */}
      {!loading && (
        <div className="history-list">
          {[...data].reverse().map(day => {
            const pct = calorieGoal > 0 ? Math.round((day.total_calories / calorieGoal) * 100) : 0;
            const colorClass = getBarColor(day.total_calories);

            return (
              <div key={day.date} className="history-day-row">
                <div
                  className="history-day-main"
                  onClick={() => navigate(day.date === todayStr ? '/log' : `/log/${day.date}`)}
                >
                  <div className="history-day-info">
                    <span className="history-day-label">{formatDayLabel(day.date)}</span>
                    {day.entry_count > 0 && (
                      <span className="history-day-entries">{day.entry_count} item{day.entry_count !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="history-day-stats">
                    <span className={`history-day-cal ${colorClass}`}>
                      {day.total_calories > 0 ? `${day.total_calories} cal` : '--'}
                    </span>
                    <span className={`history-day-pct ${colorClass}`}>
                      {day.total_calories > 0 ? `${pct}%` : ''}
                    </span>
                    {day.water_ml > 0 && (
                      <span className="history-day-water">
                        &#128167; {day.water_ml >= 1000 ? `${(day.water_ml / 1000).toFixed(1)}L` : `${day.water_ml}ml`}
                      </span>
                    )}
                  </div>
                </div>
                {day.entry_count > 0 && (
                  <button
                    className="history-copy-btn"
                    onClick={(e) => openCopyFrom(e, day.date)}
                    title="Copy this day"
                  >
                    &#x2398;
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Copy Day Mini Modal */}
      {copyFromDate && (
        <div className="copy-overlay" onClick={() => setCopyFromDate(null)}>
          <div className="copy-panel copy-panel-compact" onClick={e => e.stopPropagation()}>
            <div className="copy-panel-header">
              <h3>Copy Day</h3>
              <button className="copy-panel-close" onClick={() => setCopyFromDate(null)}>&times;</button>
            </div>

            <p className="copy-panel-source">
              From: <strong>{formatDayLabel(copyFromDate)}</strong>
            </p>

            <div className="copy-panel-field">
              <label>Copy to date</label>
              <input
                type="date"
                className="date-input"
                value={copyTargetDate}
                onChange={(e) => setCopyTargetDate(e.target.value)}
              />
            </div>

            {copyTargetDate === copyFromDate && (
              <div className="copy-warning copy-warning-error">
                Cannot copy to the same date.
              </div>
            )}

            <button
              className="btn btn-primary btn-full"
              onClick={handleCopy}
              disabled={copyLoading || copyTargetDate === copyFromDate}
            >
              {copyLoading ? 'Copying...' : 'Copy All Meals'}
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default History;
